import {
  answerToText,
  questionBlocks,
  type QuestionGroup,
  assembleQuestionAnswer,
  visibleRepeatedAnswer,
  answerValueSchema,
  visibleQuestions,
  walkSections,
  takesAnswer,
  type FormContent,
  type AnswerMap,
} from "@sfc/forms";
import type { Field, Proposal, ProposalForm } from "@engine/round/aggregate";

export function applicationResponseProposalId(
  proposalFormId: string,
  responseId: string,
): string {
  return `p-application-${proposalFormId}-${responseId}`;
}

/** Provenance survives detaching a form; a spreadsheet proposal never opts in. */
export function isApplicationBuilderProposal(
  proposal: Pick<Proposal, "id" | "externalId">,
  proposalFormId: string,
): boolean {
  return (
    !!proposal.externalId &&
    proposal.id ===
      applicationResponseProposalId(proposalFormId, proposal.externalId)
  );
}

/** Render from the pinned submission, never from inclusion-time copies. */
export function applicationProposalSections(
  content: FormContent,
  answers: AnswerMap,
) {
  const asked = visibleQuestions(content, answers).filter((q) =>
    takesAnswer(q.type),
  );
  return walkSections(content, answers).map((section) => ({
    id: section.id,
    title: section.title,
    groups: content.groups?.filter(
      (group) => group.sectionId === section.id && !group.repeat,
    ),
    questions: asked
      .filter((q) => q.sectionId === section.id)
      .map((question) => {
        const value = visibleRepeatedAnswer(
          question,
          assembleQuestionAnswer(question, answers[question.id], answers),
        );
        const rich =
          question.type === "file_upload" ||
          question.type === "repeatable_group";
        return {
          id: question.id,
          groupId: question.groupId,
          title: question.label,
          description: question.help,
          answer: answerToText(question, value),
          fileQuestion: rich ? question : undefined,
          fileAnswer: rich ? value : undefined,
        };
      }),
  }));
}

/**
 * Saved rows as an `AnswerMap`. Values are shape-checked on the way out: a
 * row written by a build with a different answer shape must not crash the
 * walk — an unreadable value reads as "not answered", same as submit treats
 * it.
 *
 * Exported because the admin's read of a response needs exactly this rule,
 * and a second copy of it is a copy that drifts: the two would then disagree
 * about what an unreadable row means, which is the one thing this function
 * decides.
 */
export function answersFromRows(
  rows: { questionId: string; value: unknown }[],
): AnswerMap {
  const answers: AnswerMap = {};
  for (const row of rows) {
    const parsed = answerValueSchema.safeParse(row.value);
    if (parsed.success) answers[row.questionId] = parsed.data;
  }
  return answers;
}

/** Pair the pinned catalog and answers for every proposal consumer. Read-only projection. */
export function applicationProposalFieldContext(
  sections: {
    id: string;
    title: string;
    groups?: QuestionGroup[];
    questions: {
      groupId?: string;
      id: string;
      title: string;
      description?: string;
      answer: string;
    }[];
  }[],
) {
  const fields: Field[] = [];
  const fieldResponses: Record<string, string> = {};
  for (const section of sections) {
    fields.push({
      id: section.id,
      title: section.title,
      type: "sectionHeader",
      source: "appInterface",
      status: "active",
    });
    for (const block of questionBlocks(section, section.questions)) {
      if (block.group)
        fields.push({
          id: block.group.id,
          title: block.group.title,
          description: block.group.description,
          type: "sectionHeader",
          source: "appInterface",
          status: "active",
        });
      for (const question of block.questions) {
        fields.push({
          id: question.id,
          title: question.title,
          description: question.description,
          type: "question",
          source: "appInterface",
          status: "active",
        });
        fieldResponses[question.id] = question.answer;
      }
    }
  }
  return { fields, fieldResponses };
}

/** Shared in-memory content boundary for display, search and exports. A missing
 * Builder source stays unavailable; legacy answers are never its fallback.
 */
export function resolveProposalFieldContext(
  proposal: Pick<Proposal, "id" | "externalId"> | undefined,
  form: Pick<ProposalForm, "id" | "fields"> | null | undefined,
  legacyResponses: Record<string, string>,
  application:
    | ReturnType<typeof applicationProposalFieldContext>
    | null
    | undefined,
) {
  if (proposal && form && isApplicationBuilderProposal(proposal, form.id))
    return application ?? null;
  return { fields: form?.fields ?? [], fieldResponses: legacyResponses };
}
